import { useState, useEffect } from 'react';

export const useDeviceInfo = () => {
  const [deviceInfo, setDeviceInfo] = useState({
    ip: null,
    localIP: null,
    location: null,
    loading: true,
    error: null
  });

  // Función para obtener la IP pública
  const getPublicIP = async () => {
    try {
      // Intentar múltiples servicios para mayor confiabilidad
      const services = [
        'https://api.ipify.org?format=json',
        'https://ipapi.co/json/',
        'https://jsonip.com'
      ];

      for (const service of services) {
        try {
          const response = await fetch(service);
          const data = await response.json();
          
          // Diferentes servicios devuelven la IP en diferentes campos
          const ip = data.ip || data.query || data.IPv4;
          if (ip) {
            return ip;
          }
        } catch (serviceError) {
          console.warn(`Error con servicio ${service}:`, serviceError);
          continue;
        }
      }
      
      throw new Error('No se pudo obtener la IP de ningún servicio');
    } catch (error) {
      console.error('Error obteniendo IP:', error);
      return null;
    }
  };

  // Función para obtener información detallada de IP y ubicación
  const getIPInfo = async (ip) => {
    try {
      const response = await fetch(`https://ipapi.co/${ip}/json/`);
      const data = await response.json();
      
      return {
        ip: data.ip,
        city: data.city,
        region: data.region,
        country: data.country_name,
        countryCode: data.country_code,
        latitude: data.latitude,
        longitude: data.longitude,
        timezone: data.timezone,
        isp: data.org,
        asn: data.asn
      };
    } catch (error) {
      console.error('Error obteniendo información de IP:', error);
      return null;
    }
  };

  // Función para obtener geolocalización del navegador
  const getBrowserLocation = () => {
    return new Promise((resolve, reject) => {
      if (!navigator.geolocation) {
        reject(new Error('Geolocalización no soportada'));
        return;
      }

      const options = {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 300000 // 5 minutos
      };

      navigator.geolocation.getCurrentPosition(
        (position) => {
          resolve({
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
            accuracy: position.coords.accuracy,
            timestamp: position.timestamp
          });
        },
        (error) => {
          let errorMessage = 'Error desconocido';
          switch (error.code) {
            case error.PERMISSION_DENIED:
              errorMessage = 'Permisos de geolocalización denegados';
              break;
            case error.POSITION_UNAVAILABLE:
              errorMessage = 'Información de ubicación no disponible';
              break;
            case error.TIMEOUT:
              errorMessage = 'Tiempo de espera agotado para obtener ubicación';
              break;
          }
          reject(new Error(errorMessage));
        },
        options
      );
    });
  };

  // Función para obtener información adicional del dispositivo
  const getDeviceDetails = () => {
    return {
      userAgent: navigator.userAgent,
      platform: navigator.platform,
      language: navigator.language,
      cookieEnabled: navigator.cookieEnabled,
      onLine: navigator.onLine,
      screenWidth: screen.width,
      screenHeight: screen.height,
      windowWidth: window.innerWidth,
      windowHeight: window.innerHeight,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      timestamp: new Date().toISOString()
    };
  };

  // Función para obtener la IP local usando WebRTC
  const getLocalIP = () => {
    return new Promise((resolve, reject) => {
      try {
        // Crear una conexión WebRTC para obtener la IP local
        const rtc = new RTCPeerConnection({
          iceServers: [{ urls: 'stun:stun.l.google.com:19302' }]
        });

        // Crear un canal de datos dummy
        rtc.createDataChannel('');

        let localIPs = [];

        rtc.onicecandidate = (event) => {
          if (event.candidate) {
            const candidate = event.candidate.candidate;
            const ipMatch = candidate.match(/(\d+\.\d+\.\d+\.\d+)/);
            
            if (ipMatch) {
              const ip = ipMatch[1];
              // Filtrar IPs locales válidas (evitar 0.0.0.0 y otras no válidas)
              if (ip !== '0.0.0.0' && 
                  !ip.startsWith('169.254') && // Link-local
                  !localIPs.includes(ip)) {
                localIPs.push(ip);
              }
            }
          } else {
            // No más candidatos, resolver con las IPs encontradas
            rtc.close();
            if (localIPs.length > 0) {
              // Priorizar IPs privadas (192.168.x.x, 10.x.x.x, 172.16-31.x.x)
              const privateIPs = localIPs.filter(ip => 
                ip.startsWith('192.168.') || 
                ip.startsWith('10.') || 
                /^172\.(1[6-9]|2[0-9]|3[01])\./.test(ip)
              );
              resolve(privateIPs.length > 0 ? privateIPs[0] : localIPs[0]);
            } else {
              resolve(null);
            }
          }
        };

        rtc.onicecandidateerror = (error) => {
          console.warn('Error obteniendo IP local:', error);
          rtc.close();
          resolve(null);
        };

        // Crear oferta para iniciar el proceso de ICE
        rtc.createOffer()
          .then(offer => rtc.setLocalDescription(offer))
          .catch(error => {
            console.warn('Error creando oferta WebRTC:', error);
            rtc.close();
            resolve(null);
          });

        // Timeout después de 3 segundos
        setTimeout(() => {
          rtc.close();
          if (localIPs.length > 0) {
            const privateIPs = localIPs.filter(ip => 
              ip.startsWith('192.168.') || 
              ip.startsWith('10.') || 
              /^172\.(1[6-9]|2[0-9]|3[01])\./.test(ip)
            );
            resolve(privateIPs.length > 0 ? privateIPs[0] : localIPs[0]);
          } else {
            resolve(null);
          }
        }, 3000);

      } catch (error) {
        console.error('Error en getLocalIP:', error);
        resolve(null);
      }
    });
  };

  // Efecto principal para cargar toda la información
  useEffect(() => {
    const loadDeviceInfo = async () => {
      try {
        setDeviceInfo(prev => ({ ...prev, loading: true, error: null }));

        // Obtener información básica del dispositivo
        const deviceDetails = getDeviceDetails();

        // Obtener IP pública
        const ip = await getPublicIP();
        
        // Obtener IP local
        const localIP = await getLocalIP();
        
        let ipInfo = null;
        if (ip) {
          ipInfo = await getIPInfo(ip);
        }

        // Intentar obtener geolocalización del navegador
        let browserLocation = null;
        try {
          browserLocation = await getBrowserLocation();
        } catch (locationError) {
          console.warn('No se pudo obtener geolocalización del navegador:', locationError.message);
        }

        // Combinar toda la información
        const finalInfo = {
          ip: ip,
          localIP: localIP,
          ipInfo: ipInfo,
          browserLocation: browserLocation,
          deviceDetails: deviceDetails,
          loading: false,
          error: null
        };

        setDeviceInfo(finalInfo);

      } catch (error) {
        console.error('Error cargando información del dispositivo:', error);
        setDeviceInfo(prev => ({
          ...prev,
          loading: false,
          error: error.message
        }));
      }
    };

    loadDeviceInfo();
  }, []);

  // Función para refrescar la información
  const refreshInfo = async () => {
    setDeviceInfo(prev => ({ ...prev, loading: true }));
    const ip = await getPublicIP();
    const localIP = await getLocalIP();
    const ipInfo = ip ? await getIPInfo(ip) : null;
    
    try {
      const browserLocation = await getBrowserLocation();
      setDeviceInfo(prev => ({
        ...prev,
        ip,
        localIP,
        ipInfo,
        browserLocation,
        loading: false,
        error: null
      }));
    } catch (error) {
      setDeviceInfo(prev => ({
        ...prev,
        ip,
        localIP,
        ipInfo,
        loading: false,
        error: error.message
      }));
    }
  };

  return {
    ...deviceInfo,
    refreshInfo
  };
};

export default useDeviceInfo;
